import React, { useState } from "react";
import logo from "../../../assets/logo.png";
import "./Login.css";
import { useApi } from "../../../hooks/useApi"; 

export default function Login({ onLoginSuccess, onGoToRegister }) {
  const { request, loading } = useApi(); 
  
  const [step, setStep] = useState(0); 
  const [mensaje, setMensaje] = useState({ text: "", type: "" });

  const [correo, setCorreo] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const getAlertStyles = (type) => {
    switch (type) {
      case "success": return "bg-green-50/90 border-green-200 text-green-800";
      case "warning": return "bg-amber-50/90 border-amber-200 text-amber-800";
      case "error": default: return "bg-red-50/90 border-red-200 text-red-800";
    }
  };

  // ==========================================================
  // 1. INICIAR SESIÓN
  // ==========================================================
  const handleLogin = async (e) => {
    e.preventDefault();
    setMensaje({ text: "", type: "" });
    
    // Petición limpia en 1 línea
    const { success, data } = await request('/auth/login', 'POST', { 
      correo: correo.trim().toLowerCase(), 
      password 
    });

    if (success) {
      setMensaje({ text: "¡Ingreso exitoso!", type: "success" });
      if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
      setTimeout(() => { if (onLoginSuccess) onLoginSuccess(data.user); }, 500);
    } else {
      setMensaje({ text: "Correo o contraseña incorrectos.", type: "error" });
    }
  };

  // ==========================================================
  // 2. SOLICITAR CÓDIGO
  // ==========================================================
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setMensaje({ text: "", type: "" });
    
    // Le pasamos "true" al final para que el Hook muestre la alerta de éxito automático
    const { success } = await request('/auth/recuperar/solicitar', 'POST', { 
      correo: recoveryEmail.trim().toLowerCase() 
    }, true);

    if (success) setStep(2);
  };

  // ==========================================================
  // 3. VALIDAR CÓDIGO
  // ==========================================================
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setMensaje({ text: "", type: "" });
    
    const { success } = await request('/auth/recuperar/validar', 'POST', { 
      correo: recoveryEmail.trim().toLowerCase(), 
      codigo: recoveryCode 
    }, true);

    if (success) setStep(3);
  };

  // ==========================================================
  // 4. CAMBIAR CONTRASEÑA
  // ==========================================================
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMensaje({ text: "", type: "" });
    
    const { success } = await request('/auth/recuperar/cambiar', 'POST', { 
      correo: recoveryEmail.trim().toLowerCase(), 
      codigo: recoveryCode, 
      newPassword 
    }, true);

    if (success) {
      setTimeout(() => { 
        setStep(0); 
        setRecoveryEmail(""); 
        setRecoveryCode(""); 
        setNewPassword(""); 
      }, 3000);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card relative overflow-hidden transition-all duration-300">
        
        {step > 0 && (
          <button onClick={() => { setStep(0); setMensaje({ text: "", type: "" }); }} className="absolute top-4 left-4 text-gray-500 hover:text-gray-900 font-bold flex items-center gap-1 transition-colors">
            <span></span> Volver
          </button>
        )}

        <div className="flex justify-center mb-6 w-full">
          <img src={logo} alt="Logo" className="h-16 w-auto object-contain drop-shadow-md" />
        </div>

        {mensaje.text && (
          <div className={`mb-6 w-full px-4 py-3 rounded-2xl text-sm font-medium text-center border backdrop-blur-md shadow-sm animate-fade-in-up ${getAlertStyles(mensaje.type)}`}>
            {mensaje.text}
          </div>
        )}

        {/* PASO 0: LOGIN */}
        {step === 0 && (
          <>
            <div className="text-center mb-6 w-full">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Iniciar Sesión</h2>
              <p className="text-sm text-gray-800 font-medium mt-1">Ingresa tus credenciales para continuar</p>
            </div>
            <form onSubmit={handleLogin} className="w-full space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wide mb-2 pl-2">Correo Electrónico</label>
                <input type="email" placeholder="correo@ejemplo.com" value={correo} onChange={(e) => setCorreo(e.target.value)} required className="w-full px-5 py-4 bg-white/60 border border-white/70 rounded-2xl text-gray-900 placeholder-gray-500 font-medium focus:bg-white/90 focus:border-[#007aff] focus:ring-4 focus:ring-[#007aff]/20 transition-all duration-300 outline-none shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wide mb-2 pl-2">Contraseña</label>
                <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-5 py-4 bg-white/60 border border-white/70 rounded-2xl text-gray-900 placeholder-gray-500 font-medium focus:bg-white/90 focus:border-[#007aff] focus:ring-4 focus:ring-[#007aff]/20 transition-all duration-300 outline-none shadow-sm" />
              </div>
              <div className="pt-2">
                <button type="submit" disabled={loading} className={`w-full py-4 text-white font-extrabold text-[15px] rounded-full transition-all duration-300 flex justify-center items-center ${loading ? "bg-blue-400 cursor-not-allowed opacity-80" : "bg-[#007aff] hover:bg-[#0062cc] shadow-[0_8px_20px_rgba(0,122,255,0.3)] hover:-translate-y-1 active:scale-[0.98]"}`}>
                  {loading ? "Ingresando..." : "Ingresar"}
                </button>
              </div>
            </form>
            
            <div className="mt-6 text-center w-full flex flex-col gap-3">
              <button type="button" onClick={onGoToRegister} className="text-sm font-bold text-[#005bb5] hover:text-[#003d82] transition-colors hover:underline">
                ¿Nuevo colaborador? Regístrate aquí
              </button>
              <button type="button" onClick={() => { setStep(1); setMensaje({ text: "", type: "" }); }} className="text-xs font-bold text-gray-500 hover:text-gray-800 transition-colors cursor-pointer hover:underline">
                ¿Olvidaste tu contraseña? Recupérala aquí
              </button>
            </div>
          </>
        )}

        {/* PASO 1: SOLICITAR CORREO */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="w-full space-y-5 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Recuperar Contraseña</h2>
              <p className="text-sm text-gray-600 mt-2">Ingresa tu correo para recibir un código de seguridad de 6 dígitos.</p>
            </div>
            <input type="email" placeholder="Ingresa tu correo" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} required className="w-full px-5 py-4 bg-white/60 border border-white/70 rounded-2xl text-gray-900 font-medium focus:border-[#007aff] focus:ring-4 focus:ring-[#007aff]/20 transition-all outline-none" />
            <button type="submit" disabled={loading} className="w-full py-4 bg-[#007aff] hover:bg-[#0062cc] text-white font-extrabold rounded-full shadow-md transition-all active:scale-[0.98]">
              {loading ? "Enviando..." : "Enviar Código"}
            </button>
          </form>
        )}

        {/* PASO 2: VALIDAR CÓDIGO */}
        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="w-full space-y-5 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Ingresa el Código</h2>
              <p className="text-sm text-gray-600 mt-2">Hemos enviado un código de 6 dígitos a <b className="text-gray-800">{recoveryEmail}</b>.</p>
            </div>
            <input type="text" maxLength={6} placeholder="------" value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, ''))} required className="w-full px-5 py-4 bg-white/60 border border-white/70 rounded-2xl text-center text-2xl tracking-[0.5em] text-gray-900 font-black focus:border-[#007aff] transition-all outline-none" />
            <button type="submit" disabled={loading || recoveryCode.length < 6} className="w-full py-4 bg-[#007aff] text-white font-extrabold rounded-full shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Validando..." : "Validar Código"}
            </button>
          </form>
        )}

        {/* PASO 3: NUEVA CONTRASEÑA */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="w-full space-y-5 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Nueva Contraseña</h2>
              <p className="text-sm text-gray-600 mt-2">Asigna una nueva contraseña para tu cuenta.</p>
            </div>
            <input type="password" minLength={6} placeholder="Nueva contraseña" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="w-full px-5 py-4 bg-white/60 border border-white/70 rounded-2xl text-gray-900 font-medium focus:border-[#007aff] focus:ring-4 focus:ring-[#007aff]/20 transition-all outline-none" />
            <button type="submit" disabled={loading} className="w-full py-4 bg-[#007aff] hover:bg-[#0062cc] text-white font-extrabold rounded-full shadow-md transition-all active:scale-[0.98]">
              {loading ? "Actualizando..." : "Cambiar Contraseña"}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}